import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FaXmark as XMark } from 'react-icons/fa6'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  /** Fires only when the user clicks the confirm button. Cancel / Esc /
   *  overlay click call `onClose` only. */
  onConfirm: () => void
  title: string
  /** Supporting copy under the title. Plain text — no markup. */
  description?: string
  /** Label for the primary action. Defaults to "Confirm". */
  confirmLabel?: string
  /** Label for the cancel action. Defaults to "Cancel". */
  cancelLabel?: string
  /** Style the confirm button as destructive (red). Defaults to false. */
  destructive?: boolean
  /** Disable the confirm button (e.g. mutation in flight). */
  pending?: boolean
}

/**
 * Reusable in-app confirmation modal. Replaces `window.confirm` for
 * destructive actions — native dialogs can be permanently silenced by
 * Safari's "block further alerts" checkbox, which leaves our delete
 * button looking broken. A Radix dialog is uncancellable from the
 * browser chrome and always renders, so the confirmation is guaranteed
 * to reach the user.
 *
 * Usage:
 *   <ConfirmDialog
 *     isOpen={target !== null}
 *     onClose={() => setTarget(null)}
 *     onConfirm={() => { doDelete(target); setTarget(null) }}
 *     title={`Delete "${target?.name}"?`}
 *     description="This removes every pattern in the project."
 *     confirmLabel="Delete"
 *     destructive
 *   />
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  pending = false,
}) => {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          <header className="flex items-start justify-between px-5 py-4 border-b border-brd-subtle">
            <Dialog.Title className="text-base font-semibold text-content-primary">
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="p-1.5 rounded-md text-content-secondary hover:text-content-primary hover:bg-surface-sunken transition-colors"
              >
                <XMark size={14} />
              </button>
            </Dialog.Close>
          </header>
          {description && (
            <div className="px-5 py-3 text-sm text-content-secondary">
              <Dialog.Description>{description}</Dialog.Description>
            </div>
          )}
          <footer className="flex items-center justify-end gap-2 px-5 py-4 border-t border-brd-subtle">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="btn-secondary btn-sm disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              autoFocus
              onClick={onConfirm}
              disabled={pending}
              className={
                destructive
                  ? 'btn btn-sm bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50'
                  : 'btn-primary btn-sm disabled:opacity-50'
              }
            >
              {pending ? 'Working…' : confirmLabel}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
